import {
  AssistantCitation,
  KnowledgeAskCitation,
  WebCitation,
} from '@digital-twins/shared-types'

/**
 * Either citation species a turn's `citations` array can hold.
 *
 * `KnowledgeAskCitation` is what the legacy grounded ask persists (no `kind`
 * field at all). `AssistantCitation` is what an assistant turn persists,
 * tagged `kind: 'document' | 'web'` — `KnowledgeThreadMessage.citations` in
 * `apps/frontend/src/lib/api/client.ts` is typed as exactly this union,
 * because the server writes both shapes into the same jsonb column. A reader
 * must partition on `kind` before handing citations to any renderer: a web
 * citation put through the document pipeline has no document name, no
 * anchors and no page.
 */
export type AnyThreadCitation = KnowledgeAskCitation | AssistantCitation

export interface PartitionedAssistantCitations {
  /**
   * Legacy `KnowledgeAskCitation` citations AND `kind: 'document'` assistant
   * citations, indistinguishable once partitioned: `buildAssistantCitations`
   * (`apps/backend/src/knowledge/knowledge.service.ts`) enriches a `document`
   * citation through the SAME `enrichGroundedCitations` + `insertCitations`
   * path a grounded ask uses before tagging it `kind: 'document'`, so the
   * object on the wire carries every `KnowledgeAskCitation` field
   * (`extractionVersion`, `charStart`, `unitCharStart`, `citationToken`, …)
   * even though the shared `DocumentCitation` type only declares the handful
   * of fields unique to the wire contract. Casting here is honest, not a
   * widening: it lets every existing document renderer
   * (`groupCitationsByDocument`, `injectCitationMarkers`, `ThreadAnswerBody`,
   * `ThreadSourceCard`, `ThreadSourceView`) keep taking
   * `Array<KnowledgeAskCitation>` completely unchanged.
   *
   * Renumbered densely — 1..N of this array alone: nothing downstream needs
   * a document citation's position in the original mixed array, and inline
   * `[n](#cite-n)` chips only ever exist for this half in the first place (a
   * web citation carries no `charStart`/`charEnd`/`snippet` to anchor one).
   * A turn with no web citations therefore renders byte-identical numbering
   * to today.
   */
  documentCitations: Array<KnowledgeAskCitation>
  /**
   * `kind: 'web'` citations, in their original relative order. Never
   * anchored inline (spans do not exist on the wire) — render them as a
   * distinct, URL-keyed card instead of feeding them to any document
   * renderer. See `ThreadWebSourceCard.tsx`.
   */
  webCitations: Array<WebCitation>
}

function isCitationLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** True for a `kind: 'web'` assistant citation. */
export function isWebAssistantCitation(
  citation: AnyThreadCitation,
): citation is WebCitation & { kind: 'web' } {
  return isCitationLike(citation) && citation.kind === 'web'
}

/**
 * True for everything the existing document pipeline can render: a legacy
 * `KnowledgeAskCitation` (no `kind` at all) or a `kind: 'document'` assistant
 * citation. Mirrors the backend's own split
 * (`documentRaw = raw.filter((c) => c.kind !== 'web')` in
 * `knowledge.service.ts:buildAssistantCitations`) so a future third `kind`
 * falls through to the same bucket on both sides of the wire instead of
 * silently vanishing on just one.
 */
export function isDocumentAssistantCitation(
  citation: AnyThreadCitation,
): citation is KnowledgeAskCitation {
  return isCitationLike(citation) && !isWebAssistantCitation(citation)
}

function toWebCitation(citation: WebCitation & { kind: 'web' }): WebCitation {
  const url = typeof citation.url === 'string' ? citation.url.trim() : ''
  const rawTitle = typeof citation.title === 'string' ? citation.title.trim() : ''
  const publisher =
    typeof citation.publisher === 'string' && citation.publisher.trim()
      ? citation.publisher.trim()
      : undefined
  const publishedAt =
    typeof citation.publishedAt === 'string' && citation.publishedAt.trim()
      ? citation.publishedAt.trim()
      : undefined

  return {
    url,
    // A titleless web hit still needs SOMETHING to render and to group by;
    // the url is the only field guaranteed present on a real web citation.
    title: rawTitle || url,
    publisher,
    publishedAt,
  }
}

/**
 * Split a turn's raw citation list by kind, BEFORE any rendering.
 *
 * This is the ONE seam both the live stream path (the `citations` / `done`
 * frame from `assistantTurnStream`) and `messageToTurn` (reload, from
 * `KnowledgeThreadMessage.citations`) must call. Neither caller should ever
 * hand a mixed array straight to a document renderer again — see the
 * defensive backstops left in `groupCitationsByDocument.ts` and
 * `injectCitationMarkers.ts` for what happens if one does anyway.
 *
 * Never throws: a non-object entry (null, a stray primitive) is dropped
 * rather than crashing the turn.
 */
export function partitionAssistantCitations(
  citations: Array<AnyThreadCitation> | null | undefined,
): PartitionedAssistantCitations {
  const documentCitations: Array<KnowledgeAskCitation> = []
  const webCitations: Array<WebCitation> = []

  for (const citation of citations ?? []) {
    if (!isCitationLike(citation)) {
      continue
    }
    if (isWebAssistantCitation(citation)) {
      webCitations.push(toWebCitation(citation))
      continue
    }
    documentCitations.push(citation as KnowledgeAskCitation)
  }

  return { documentCitations, webCitations }
}
